import { Form } from 'antd';
import { FC, ReactElement, memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { PageNodeFormFields, PageNodeFormInputs, useEditPageNodeMutation, useGetByIdPageNodeQuery } from '@/modules/Pages';

import { PageNodesFormView } from './PageNodesFormView';
import { pageNodesValidationSchema } from './validation';

interface Props {
  siteId: string;
  nodeId: string;
}

export const PageNodesEditFormContainer: FC<Props> = memo(({ nodeId, siteId }) => {
  const navigate = useNavigate();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<PageNodeFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PageNodeFormInputs>>(null);

  const {
    data: sitePageNodeData,
    error: sitePageNodeError,
    isLoading: isPageNodeLoading,
    isError: isPageNodeError,
    refetch: refetchPageNode
  } = useGetByIdPageNodeQuery(
    siteId,
    nodeId
  )({
    enabled: !!siteId && !!nodeId
  });

  const { mutate: editPageNodeMutation, isLoading: editPageNodeIsLoading } = useEditPageNodeMutation(
    nodeId,
    siteId
  )({
    onSuccess: async (): Promise<void> => {
      appMessageApi.success('Ноду змінено успішно!');

      await refetchPageNode();

      navigate(AppRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', nodeId));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof PageNodeFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: PageNodeFormFields = {
    slug: sitePageNodeData?.slug,
    extension: sitePageNodeData?.extension ?? undefined,
    parentNodeId: sitePageNodeData?.parentNode?.id ?? undefined,
    siteId
  };

  const handleFinish = useCallback((values: PageNodeFormFields): void => {
    editPageNodeMutation(values);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', nodeId));
  }, [siteId, nodeId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <FallbackedLayout
      data={ sitePageNodeData }
      error={ sitePageNodeError }
      isError={ isPageNodeError }
      isLoading={ isPageNodeLoading }
    >
      { (sitePageNode): ReactElement =>
        <PageNodesFormView
          isNodeHome={ sitePageNode.isHome }
          siteId={ siteId }
          formErrors={ formErrors }
          validationSchema={ pageNodesValidationSchema }
          formName={ 'page-node-update' }
          formType={ 'edit' }
          form={ form }
          initialValues={ initialValues }
          isFormLoading={ editPageNodeIsLoading }
          handleBack={ handleBack }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      }
    </FallbackedLayout>
  );
});
